import React from "react";
import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dateLocale } from "i18n";

const formatShortDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
  });
};

const AttendanceGrid = ({
  players,
  sessions,
  attendance,
  onPlayerSelect,
  selectedPlayerId,
}) => {
  const { t } = useTranslation();
  // Build attendance lookup set
  const attendanceSet = new Set(
    (attendance || []).map((a) => `${a.player_id}:${a.session_id}`)
  );

  const isPresent = (playerId, sessionId) =>
    attendanceSet.has(`${playerId}:${sessionId}`);

  if (sessions.length === 0 || players.length === 0) {
    return (
      <div className="rounded-xl border border-[#F3F4F6] bg-white">
        <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
          <h3 className="text-sm font-semibold text-[#111827]">
            {t("team.attendanceGrid.title")}
          </h3>
        </div>
        <div className="flex h-[200px] flex-col items-center justify-center px-6 text-center">
          <p className="text-sm font-semibold text-[#111827]">
            {t("team.attendanceGrid.emptyTitle")}
          </p>
          <p className="mt-1 text-xs text-[#6B7280]">
            {t("team.attendanceChart.emptyBody")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white">
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <h3 className="text-sm font-semibold text-[#111827]">
          Attendance Matrix
        </h3>
      </div>

      <div className="max-h-[300px] overflow-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-white">
            <tr>
              {/* Sticky player column header */}
              <th className="sticky left-0 z-20 min-w-[160px] border-b border-r border-[#F3F4F6] bg-[#F9FAFB] px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]">
                {t("team.attendanceGrid.athleteColumn")}
              </th>
              {sessions.map((session) => {
                const isMatch =
                  session.type === "match" || session.session_type === "match";
                return (
                  <th
                    key={session.id}
                    className="min-w-[56px] border-b border-[#F3F4F6] px-2 py-2 text-center text-[10px] font-medium"
                    style={{ color: isMatch ? "#C07030" : "#6B7280" }}
                  >
                    {formatShortDate(session.date || session.created_at)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {players.map((player) => {
              const isSelected = player.id === selectedPlayerId;
              return (
                <tr
                  key={player.id}
                  onClick={() => onPlayerSelect?.(player)}
                  className={`cursor-pointer transition duration-150 ${
                    isSelected ? "bg-[#FEF7F3]" : "hover:bg-[#FAFAFA]"
                  }`}
                >
                  {/* Sticky player name column */}
                  <td
                    className={`sticky left-0 z-[5] border-b border-r border-[#F3F4F6] px-3 py-2 ${
                      isSelected ? "bg-[#FEF7F3]" : "bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate text-[12px] font-medium text-[#111827]">
                        {player.name}
                      </span>
                      <span className="ml-2 flex-shrink-0 text-[10px] font-medium text-[#6B7280]">
                        {player.rate}%
                      </span>
                    </div>
                  </td>
                  {sessions.map((session) => {
                    const present = isPresent(player.id, session.id);
                    return (
                      <td
                        key={session.id}
                        className="border-b border-[#F3F4F6] px-2 py-2 text-center"
                      >
                        {present ? (
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-[#FEF3E7]">
                            <Check size={10} className="text-[#E07A3A]" />
                          </span>
                        ) : (
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-[#F3F4F6]">
                            <X size={10} className="text-[#9CA3AF]" />
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceGrid;
